/**
 * 治療内容として独立性の高い施術の「個別施術詳細ページ」用データ。
 * NOTE: 正式な効果・製剤名・使用量・照射条件・施術時間・ダウンタイム・副作用・料金は未確定のため、
 *       ここでは推測した医療情報を作成しない。各セクションは「後から入力できるプレースホルダー」として扱う。
 *       お悩み・部位は、既存カテゴリページに掲載済みの事実情報のみを使用する。
 */

export type SubTreatmentAnchor = {
  label: string;
  /** 親カテゴリページ内の既存アンカーID */
  target: string;
};

export type SubTreatmentPage = {
  /** ルートのカテゴリセグメント（slim / regenerative / injectables / eye） */
  category: string;
  slug: string;
  /** 個別施術ページのパス */
  path: string;
  /** 料金表のタブID */
  priceTab: string;
  categoryJp: string;
  /** 親カテゴリページのパス */
  categoryPath: string;
  /** 親カテゴリページへ戻る導線のラベル */
  backLabel: string;
  en: string;
  jp: string;
  heroLead: string;
  heroImageAlt: string;
  heroNote: string;
  /** このようなお悩みの方へ（カテゴリページ掲載の事実情報） */
  concerns: string[];
  /** 対応部位から親カテゴリページの該当アンカーへ移動するナビ（注入治療など） */
  anchorNav?: {
    en: string;
    jp: string;
    lead?: string;
    items: SubTreatmentAnchor[];
  };
  /** 事実として明記できる補足（誤解を招かないための注意書きなど） */
  aboutNote?: string;
  ctaLabel: string;
};

const SLIM_CONCERNS = [
  "部分痩せしたい",
  "落ちにくい脂肪が気になる",
  "体型を整えたい",
  "運動や食事制限だけでは難しい",
  "部位ごとにアプローチしたい",
  "ライフスタイルに合わせて続けたい",
];

const INJECTABLES_BOTOX_AREAS: SubTreatmentAnchor[] = [
  { label: "額", target: "inj-botox-forehead" },
  { label: "眉間", target: "inj-botox-glabella" },
  { label: "バニー", target: "inj-botox-bunny" },
  { label: "目尻", target: "inj-botox-crow" },
  { label: "たれ目", target: "inj-botox-droop" },
  { label: "人中", target: "inj-botox-philtrum" },
  { label: "口角", target: "inj-botox-mouth" },
  { label: "ガミー", target: "inj-botox-gummy" },
  { label: "アゴ", target: "inj-botox-chin" },
  { label: "エラ", target: "inj-botox-masseter" },
  { label: "ワキ", target: "inj-botox-underarm" },
  { label: "肩", target: "inj-botox-shoulder" },
  { label: "ふくらはぎ", target: "inj-botox-calf" },
];

const INJECTABLES_HA_AREAS: SubTreatmentAnchor[] = [
  { label: "涙袋", target: "inj-ha-tear" },
  { label: "鼻", target: "inj-ha-nose" },
  { label: "唇", target: "inj-ha-lip" },
  { label: "顎", target: "inj-ha-chin" },
  { label: "首のしわ", target: "inj-ha-neck" },
  { label: "ほうれい線", target: "inj-ha-nasolabial" },
  { label: "口角", target: "inj-ha-mouth" },
  { label: "小じわ", target: "inj-ha-fine" },
  { label: "額", target: "inj-ha-forehead" },
  { label: "こめかみ", target: "inj-ha-temple" },
];

export const subTreatmentPages: SubTreatmentPage[] = [
  /* ------------------------------ Medical Slim ZERO ------------------------------ */
  {
    category: "slim",
    slug: "injection",
    path: "/slim/injection",
    priceTab: "slim-zero",
    categoryJp: "医療痩身",
    categoryPath: "/treatment/slim-zero",
    backLabel: "Medical Slim ZEROへ戻る",
    en: "NEW FAT-DISSOLVING INJECTION",
    jp: "新脂肪溶解注射",
    heroLead:
      "Medical Slim ZEROで検討する主要な治療のひとつです。",
    heroImageAlt: "新脂肪溶解注射の施術イメージ",
    heroNote: "",
    concerns: SLIM_CONCERNS,
    ctaLabel: "新脂肪溶解注射についてLINEで相談",
  },
  {
    category: "slim",
    slug: "rf",
    path: "/slim/rf",
    priceTab: "slim-zero",
    categoryJp: "医療痩身",
    categoryPath: "/treatment/slim-zero",
    backLabel: "Medical Slim ZEROへ戻る",
    en: "RF",
    jp: "RF（ラジオ波）",
    heroLead:
      "Medical Slim ZEROで検討する主要な治療のひとつです。",
    heroImageAlt: "RF（ラジオ波）の施術イメージ",
    heroNote: "",
    concerns: SLIM_CONCERNS,
    ctaLabel: "RF（ラジオ波）についてLINEで相談",
  },
  {
    category: "slim",
    slug: "cooling",
    path: "/slim/cooling",
    priceTab: "slim-zero",
    categoryJp: "医療痩身",
    categoryPath: "/treatment/slim-zero",
    backLabel: "Medical Slim ZEROへ戻る",
    en: "FAT COOLING",
    jp: "脂肪冷却",
    heroLead:
      "Medical Slim ZEROで検討する主要な治療のひとつです。",
    heroImageAlt: "脂肪冷却の施術イメージ",
    heroNote: "",
    concerns: SLIM_CONCERNS,
    ctaLabel: "脂肪冷却についてLINEで相談",
  },
  {
    category: "slim",
    slug: "ems",
    path: "/slim/ems",
    priceTab: "slim-zero",
    categoryJp: "医療痩身",
    categoryPath: "/treatment/slim-zero",
    backLabel: "Medical Slim ZEROへ戻る",
    en: "MEDICAL EMS",
    jp: "医療用EMS",
    heroLead:
      "Medical Slim ZEROで検討する主要な治療のひとつです。",
    heroImageAlt: "医療用EMSの施術イメージ",
    heroNote: "",
    concerns: SLIM_CONCERNS,
    ctaLabel: "医療用EMSについてLINEで相談",
  },

  /* ------------------------------ 二重・目元 ------------------------------ */
  {
    category: "eye",
    slug: "double-eyelid",
    path: "/eye/double-eyelid",
    priceTab: "eye",
    categoryJp: "二重・目元",
    categoryPath: "/treatment/eye",
    backLabel: "二重・目元へ戻る",
    en: "DOUBLE EYELID",
    jp: "二重埋没法",
    heroLead:
      "目元の施術として、二重埋没法をご案内しています。",
    heroImageAlt: "二重埋没法の施術イメージ",
    heroNote: "",
    concerns: [
      "二重にしたい",
      "目元を整えたい",
      "埋没法が気になる",
      "自然な仕上がりを希望したい",
      "ダウンタイムが気になる",
    ],
    ctaLabel: "二重埋没法についてLINEで相談",
  },
];

export function getSubTreatmentPage(category: string, slug: string) {
  return subTreatmentPages.find((page) => page.category === category && page.slug === slug);
}